import { isEqual } from 'lodash-es';
import { debounce, throttle } from 'lodash-es';
import { PdfViewerPageLayer } from '../components/pdf-viewer-page/pdf-viewer-page.component.type';
import { PageActionType, determinePageAction } from './pdf-viewer-layout-helper';
import { PdfViewerPageEventType } from '../components/pdf-viewer-page/pdf-viewer-page-events.type';
import { LoadState } from '../+types/load-state.type';
import { PdfViewerLayoutType } from '../+types/pdf-viewer-layout.type';
class RectUtils {
    static combine(rects) {
        const bounds = {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 0,
            width: 0,
            height: 0
        };
        rects.forEach(rect => {
            bounds.x1 = Math.min(bounds.x1, rect.x1);
            bounds.y1 = Math.min(bounds.y1, rect.y1);
            bounds.x2 = Math.max(bounds.x2, rect.x2);
            bounds.y2 = Math.max(bounds.y2, rect.y2);
        });
        bounds.width = bounds.x2 - bounds.x1;
        bounds.height = bounds.y2 - bounds.y1;
        return bounds;
    }
}
export class PdfViewerLayoutOneColumn {
    constructor(eventBus, pdfViewerConfig) {
        this.eventBus = eventBus;
        this.pdfViewerConfig = pdfViewerConfig;
        this.type = PdfViewerLayoutType.OneColumn;
        this._pageIdsInViewport = [];
        this._pageIdsEnteredViewport = [];
        this._pageIdsLeftViewport = [];
        this._zoom = 1;
        this.pagesPadding = 10;
        this.pageMarginBottom = 14;
        this.zoomIncrement = 0.2;
        this.defaultPageDimensions = { width: 600, height: 600 };
        this.pageIdsRendered = new Set();
        this.isScrolling = false;
        this.isZooming = false;
        this.onInitialPageLoadStateChange = (event) => {
            const { pageId, loadState } = event.payload;
            if (loadState === LoadState.Finished || loadState === LoadState.Failed) {
                this.eventBus.off(PdfViewerPageEventType.LoadStateChange, this.onInitialPageLoadStateChange);
                this.healPageConfigDimensions(pageId);
                this.updateViewport();
                this.updatePagesLayout();
                this.zoomToFitWidth(pageId);
                this.updatePagesLayout(true);
                this.goto(pageId);
                this.updateViewport();
                this.updatePageIdsInViewport();
                this.orchestratePages();
                this.addEventListeners();
                this.containerElement.classList.remove('sp-pdf-viewer__layout-one-column--hidden');
            }
        };
        this.onPdfViewerPageLoadStateChange = (event) => {
            this.debouncedOrchestratePagesOnNextTick();
        };
        this.onPdfViewerPageLayerRenderStateChange = (event) => {
            this.debouncedOrchestratePagesOnNextTick();
        };
        this.onPdfViewerPageDimensionsChange = (event) => {
            this.updatePagesLayout();
            this.updatePageIdsInViewport();
            this.updatePageIdFocused();
        };
        this.onContainerScrolling = () => {
            this.isScrolling = true;
            this.updateViewport();
            this.updatePageIdsInViewport();
            this.updatePageIdFocused();
        };
        this.onContainerScrolled = () => {
            this.isScrolling = false;
            this.debouncedOrchestratePagesOnNextTick();
        };
        this.orchestratePages = () => {
            if (this.isScrolling || this.isZooming) {
                return;
            }
            const pageAction = determinePageAction(this.pagesInDescendingOrderOfPriority);
            if (!pageAction) {
                return;
            }
            const page = pageAction.payload.page;
            const pageContainer = this.getPageContainer(page.id);
            this.pageIdsRendered.add(page.id);
            page.renderPageElement(pageContainer);
            page.renderPreview();
            switch (pageAction.type) {
                case PageActionType.Load:
                    // console.log('orchestratePages', page.pageNumber, pageAction.type);
                    page.load();
                    break;
                case PageActionType.Render:
                    // console.log('orchestratePages', page.pageNumber, pageAction.type, pageAction.payload.layer);
                    page.renderLayer(pageAction.payload.layer);
                    break;
            }
        };
        this.onZoomingEnded = () => {
            this.isZooming = false;
            this.debouncedOrchestratePagesOnNextTick();
        };
        this.onContainerScrollThrottled = throttle(this.onContainerScrolling, 25);
        this.onContainerScrollDebounced = debounce(this.onContainerScrolled, 100);
        this.debouncedOrchestratePagesOnNextTick = debounce(this.orchestratePages, 0);
        this.debouncedOrchestratePagesForZoom = debounce(this.onZoomingEnded, 500);
        this.debouncedOrchestratePagesForResize = debounce(this.debouncedOrchestratePagesOnNextTick, 500);
    }
    init(parentElement, pages, pageIdFocused) {
        this.pages = pages;
        this.pagesById = new Map();
        this.pageIndexesById = new Map();
        pages.forEach((page, index) => {
            this.pagesById.set(page.id, page);
            this.pageIndexesById.set(page.id, index);
            page.requiredLayers = new Set([
                PdfViewerPageLayer.PdfCanvas,
                PdfViewerPageLayer.PdfText,
                PdfViewerPageLayer.PdfAnnotations,
                PdfViewerPageLayer.PdfEmbeddedMedia,
                PdfViewerPageLayer.Annotations
            ]);
            page.disableEmbeddedMediaAutoPlay = true;
        });
        this.parentElement = parentElement;
        this.render();
        const page = this.pagesById.get(pageIdFocused);
        const pageContainer = this.getPageContainer(page.id);
        this.pageIdsRendered.add(page.id);
        this.eventBus.on(PdfViewerPageEventType.LoadStateChange, this.onInitialPageLoadStateChange);
        page.renderPageElement(pageContainer);
        page.renderPreview();
        page.load();
    }
    // SP-41833
    zoomToFitWidth(pageId) {
        const pageRect = this.pagesLayout.pageRects.get(pageId);
        this._zoom = (this.viewport.width - this.pagesPadding * 2) / pageRect.width;
    }
    canNavigateToPrevious() {
        return this.pageIdFocusedIndex !== 0;
    }
    canNavigateToNext() {
        return this.pageIdFocusedIndex !== this.pages.length - 1;
    }
    next() {
        const currentIndex = this.pageIdFocusedIndex;
        const nextIndex = currentIndex + 1 !== this.pages.length ? currentIndex + 1 : 0;
        this.goto(this.pages[nextIndex].id);
    }
    previous() {
        const currentIndex = this.pageIdFocusedIndex;
        const previousIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : this.pages.length - 1;
        this.goto(this.pages[previousIndex].id);
    }
    goto(pageId) {
        if (pageId === this.pageIdFocused) {
            return;
        }
        this.gotoPageId = pageId;
        if (this.pageIndexesById.get(pageId) === 0) {
            this.gotoScrollTop = 0;
            this.containerElement.scrollTop = this.gotoScrollTop;
            this.updatePageIdFocused();
            return;
        }
        const pageRect = this.pagesLayout.pageRects.get(pageId);
        this.gotoScrollTop = Math.round(Math.min(this.containerElement.scrollHeight - this.containerElementHeight, pageRect.y1 - this.pagesPadding));
        this.containerElement.scrollTop = this.gotoScrollTop;
        this.updatePageIdFocused();
    }
    zoomIn() {
        this.zoom = this.zoom + this.zoomIncrement;
    }
    zoomOut() {
        this.zoom = this.zoom - this.zoomIncrement;
    }
    destroy() {
        if (!this.containerElement) {
            return;
        }
        this.containerElement.innerHTML = '';
        this.resetPages();
        this.removeEventListeners();
        this.onContainerScrollThrottled.cancel();
        this.onContainerScrollDebounced.cancel();
        this.debouncedOrchestratePagesOnNextTick.cancel();
        this.debouncedOrchestratePagesForZoom.cancel();
        this.debouncedOrchestratePagesForResize.cancel();
        this._pageIdFocused = null;
        try {
            this.parentElement.removeChild(this.containerElement);
        }
        catch (_a) { }
    }
    getPageContainer(id) {
        return this.containerElement.querySelector(`[data-id='${id}']`);
    }
    addEventListeners() {
        this.containerElement.addEventListener('scroll', this.onContainerScrollThrottled);
        this.containerElement.addEventListener('scroll', this.onContainerScrollDebounced);
        this.eventBus.on(PdfViewerPageEventType.LoadStateChange, this.onPdfViewerPageLoadStateChange);
        this.eventBus.on(PdfViewerPageEventType.DimensionsChange, this.onPdfViewerPageDimensionsChange);
        this.eventBus.on(PdfViewerPageEventType.LayerRenderStateChange, this.onPdfViewerPageLayerRenderStateChange);
    }
    removeEventListeners() {
        this.containerElement.removeEventListener('scroll', this.onContainerScrollThrottled);
        this.containerElement.removeEventListener('scroll', this.onContainerScrollDebounced);
        this.eventBus.off(PdfViewerPageEventType.LoadStateChange, this.onInitialPageLoadStateChange);
        this.eventBus.off(PdfViewerPageEventType.LoadStateChange, this.onPdfViewerPageLoadStateChange);
        this.eventBus.off(PdfViewerPageEventType.DimensionsChange, this.onPdfViewerPageDimensionsChange);
        this.eventBus.off(PdfViewerPageEventType.LayerRenderStateChange, this.onPdfViewerPageLayerRenderStateChange);
    }
    resize() {
        this.updateViewport();
        this.updatePagesElementWidth(this.pagesLayout);
        this.updatePageIdsInViewport();
        this.updatePageIdFocused();
        this.debouncedOrchestratePagesForResize();
    }
    // TODO can we just always set the width 🤔
    updatePagesElementWidth(pagesLayout) {
        if (!pagesLayout) {
            return;
        }
        if (pagesLayout.bounds.width > this.viewport.width) {
            this.pagesElement.style.width = `${pagesLayout.bounds.width}px`;
        }
        else {
            this.pagesElement.style.width = ``;
        }
    }
    updateViewport() {
        const containerElementWidth = this.containerElementWidth;
        const containerElementHeight = this.containerElementHeight;
        const topMargin = containerElementHeight * 1;
        const bottomMargin = containerElementHeight * 1;
        const scrollLeft = this.containerElement.scrollLeft;
        const scrollTop = this.containerElement.scrollTop;
        const width = containerElementWidth;
        const height = topMargin + containerElementHeight + bottomMargin;
        this.viewport = {
            x1: scrollLeft,
            y1: scrollTop - topMargin,
            x2: scrollLeft + width,
            y2: scrollTop - topMargin + height,
            width,
            height
        };
        /*
        this.viewportElement.style.top = `${this.viewport.y1}px`;
        this.viewportElement.style.left = `${this.viewport.x1}px`;
        this.viewportElement.style.width = `${this.viewport.width}px`;
        this.viewportElement.style.height = `${this.viewport.height}px`;
        */
    }
    updatePagesLayout(alignCentre = false) {
        // First get the current scroll position to be able to properly scale it later.
        const currentScrollTop = this.containerElement.scrollTop;
        const pageRects = new Map();
        let y = this.pagesPadding;
        this.pages.forEach((page, index) => {
            const dimensions = page.dimensions || this.defaultPageDimensions;
            pageRects.set(page.id, {
                x1: 0,
                y1: y,
                x2: dimensions.width * this.zoom,
                y2: y + dimensions.height * this.zoom,
                width: dimensions.width * this.zoom,
                height: dimensions.height * this.zoom
            });
            y += dimensions.height * this.zoom;
            if (index !== this.pages.length - 1) {
                y += this.pageMarginBottom;
            }
        });
        let currentLeft = 0;
        if (this.pagesLayout) {
            currentLeft = this.containerElement.scrollLeft / (this.pagesLayout.bounds.width - this.containerElement.clientWidth);
        }
        const pagesLayout = {
            zoom: this.zoom,
            bounds: RectUtils.combine(Array.from(pageRects.values())),
            pageRects
        };
        // TODO add helper to expand Rect
        pagesLayout.bounds.x2 += this.pagesPadding * 2;
        pagesLayout.bounds.y2 += this.pagesPadding * 2;
        pagesLayout.bounds.width += this.pagesPadding * 2;
        pagesLayout.bounds.height += this.pagesPadding * 2;
        this.updatePagesElementWidth(pagesLayout);
        // TODO move to sep function...
        pagesLayout.pageRects.forEach((pageRect, id) => {
            const pageContainer = this.getPageContainer(id);
            pageContainer.style.width = `${pageRect.width}px`;
            pageContainer.style.height = `${pageRect.height}px`;
        });
        // Scale from top simplest for now
        if (this.pagesLayout) {
            this.containerElement.scrollTop = Math.round(currentScrollTop * (pagesLayout.bounds.height / this.pagesLayout.bounds.height));
        }
        if (alignCentre) {
            this.containerElement.scrollLeft = (pagesLayout.bounds.width - this.containerElement.clientWidth) / 2;
        }
        else {
            this.containerElement.scrollLeft = currentLeft * (pagesLayout.bounds.width - this.containerElement.clientWidth);
        }
        this.pagesLayout = pagesLayout;
    }
    updatePageIdsInViewport() {
        const pageIdsInViewport = this.determinePageIdsInViewport();
        if (!isEqual(this.pageIdsInViewport, pageIdsInViewport)) {
            const pageIdsEnteredViewport = pageIdsInViewport.filter(id => !this.pageIdsInViewport.includes(id));
            const pageIdsLeftViewport = this.pageIdsInViewport.filter(id => !pageIdsInViewport.includes(id));
            this.pageIdsInViewport = pageIdsInViewport;
            if (pageIdsEnteredViewport.length) {
                this.pageIdsEnteredViewport = pageIdsEnteredViewport;
            }
            if (pageIdsLeftViewport.length) {
                this.pageIdsLeftViewport = pageIdsLeftViewport;
            }
        }
    }
    determinePageIdsInViewport() {
        const pageIdsInViewport = [];
        if (this.pagesLayout) {
            this.pagesLayout.pageRects.forEach((pageRect, id) => {
                if (pageRect.y2 >= this.viewport.y1 && pageRect.y1 <= this.viewport.y2) {
                    pageIdsInViewport.push(id);
                }
            });
        }
        return pageIdsInViewport;
    }
    updatePageIdFocused() {
        if (!this.pagesLayout) {
            return;
        }
        // When navigating to item via goto
        if (this.gotoPageId &&
            this.gotoScrollTop !== null &&
            Math.abs(this.gotoScrollTop - Math.round(this.containerElement.scrollTop)) < 10) {
            if (this.pageIdFocused !== this.gotoPageId) {
                if (this.pageIdFocused) {
                    this.pageIdBlurred = this.pageIdFocused;
                }
                this.pageIdFocused = this.gotoPageId;
            }
            return;
        }
        // When navigating to item via scrolling
        this.gotoPageId = null;
        this.gotoScrollTop = null;
        const pageIdFocused = this.determinePageIdClosestToCenterOfView();
        if (this.pageIdFocused !== pageIdFocused) {
            if (this.pageIdFocused) {
                this.pageIdBlurred = this.pageIdFocused;
            }
            this.pageIdFocused = pageIdFocused;
        }
    }
    determinePageIdClosestToCenterOfView() {
        const centerY = this.containerElement.scrollTop + this.containerElementHeight / 2;
        const distances = [];
        this.pageIdsInViewport.forEach((id) => {
            const pageRect = this.pagesLayout.pageRects.get(id);
            distances.push({
                id,
                value: Math.min(Math.abs(pageRect.y1 - centerY), Math.abs(pageRect.y2 - centerY))
            });
        });
        if (distances.length) {
            return distances.sort((a, b) => a.value - b.value)[0].id;
        }
    }
    render() {
        this.containerElement = document.createElement('div');
        this.containerElement.classList.add('sp-pdf-viewer__layout-one-column', 'sp-pdf-viewer__layout-one-column--hidden');
        this.pagesElement = document.createElement('div');
        this.pagesElement.style.padding = `${this.pagesPadding}px`;
        this.pagesElement.classList.add('sp-pdf-viewer__layout-one-column__pages');
        this.viewportElement = document.createElement('div');
        this.viewportElement.classList.add('viewport');
        this.containerElement.appendChild(this.pagesElement);
        this.containerElement.appendChild(this.viewportElement);
        this.parentElement.appendChild(this.containerElement);
        let pagesHtml = '';
        this.pages.forEach((page, index) => {
            const dimensions = page.dimensions || this.defaultPageDimensions;
            const width = dimensions.width * this.zoom;
            const height = dimensions.height * this.zoom;
            const marginBottom = index !== this.pages.length - 1 ? this.pageMarginBottom : 0;
            pagesHtml += `<div data-id="${page.id}" style="width:${width}px; height:${height}px; margin-bottom:${marginBottom}px;" class="sp-pdf-viewer__layout-one-column__page"></div>`;
        });
        this.pagesElement.innerHTML = pagesHtml;
    }
    resetPages() {
        this.pages.forEach(page => page.reset());
    }
    get pagesInDescendingOrderOfPriority() {
        const pageIdsInDescendingOrderOfPriority = [this.pageIdFocused];
        const pageIdFocusedIndex = this.pageIdsInViewport.indexOf(this.pageIdFocused);
        for (let i = 1; i < this.pageIdsInViewport.length; i++) {
            pageIdsInDescendingOrderOfPriority.push(this.pageIdsInViewport[pageIdFocusedIndex + i]);
            pageIdsInDescendingOrderOfPriority.push(this.pageIdsInViewport[pageIdFocusedIndex - i]);
        }
        return pageIdsInDescendingOrderOfPriority.filter(pageId => !!pageId).map(id => this.pagesById.get(id));
    }
    set zoom(zoomLevel) {
        if (!this.pdfViewerConfig.canZoom) {
            return;
        }
        const zoom = Math.max(Math.min(zoomLevel, this.pdfViewerConfig.maxZoomScale), this.pdfViewerConfig.minZoomScale);
        if (this._zoom === zoom) {
            return;
        }
        this.isZooming = true;
        this._zoom = zoom;
        this.updatePagesLayout();
        this.updateViewport();
        this.updatePageIdsInViewport();
        this.updatePageIdFocused();
        this.pageIdsRendered.forEach(id => this.pagesById.get(id).resize());
        this.debouncedOrchestratePagesForZoom();
    }
    get zoom() {
        return this._zoom;
    }
    get pageIdsInViewport() {
        return this._pageIdsInViewport;
    }
    set pageIdsInViewport(ids) {
        this._pageIdsInViewport = ids;
    }
    get pageIdsEnteredViewport() {
        return this._pageIdsEnteredViewport;
    }
    set pageIdsEnteredViewport(ids) {
        this._pageIdsEnteredViewport = ids;
    }
    get pageIdsLeftViewport() {
        return this._pageIdsLeftViewport;
    }
    set pageIdsLeftViewport(ids) {
        this._pageIdsLeftViewport = ids;
        ids.forEach((id) => {
            if (this.pageIdsRendered.has(id)) {
                this.pageIdsRendered.delete(id);
                this.pagesById.get(id).reset();
            }
        });
    }
    get pageIdFocused() {
        return this._pageIdFocused;
    }
    set pageIdFocused(id) {
        this._pageIdFocused = id;
        const page = this.pagesById.get(id);
        page.focus();
    }
    get pageIdBlurred() {
        return this._pageIdBlurred;
    }
    set pageIdBlurred(id) {
        this._pageIdBlurred = id;
        const page = this.pagesById.get(id);
        page.blur();
    }
    get pageIdFocusedIndex() {
        return this.pageIndexesById.get(this.pageIdFocused);
    }
    get containerElementWidth() {
        if (this.containerElement) {
            return this.containerElement.clientWidth;
        }
    }
    get containerElementHeight() {
        if (this.containerElement) {
            return this.containerElement.clientHeight;
        }
    }
    /*
        Sometimes the backend will give incorrect page dimension metadata, this results in a poor experience when scrolling SP-67175.

        The metadata is only healed when:
        The page configs all have dimensions defined
        The page configs dimensions are all equal
        The page config dimensions differ from the page dimensions derived from the pdf page after its loaded.

        This can be removed when we have the dimensions in the the original and processed variations SP-67206.
    */
    healPageConfigDimensions(loadedPageId) {
        const loadedPage = this.pagesById.get(loadedPageId);
        if (this.arePageConfigsDimensionsDefinedAndEqual(this.pages) && this.arePageConfigDimensionsInvalid(loadedPage)) {
            this.updatePageConfigsDimensions(loadedPage.dimensions);
        }
    }
    arePageConfigsDimensionsDefinedAndEqual(pages) {
        return pages.every(page => page.pageConfig.dimensions &&
            page.pageConfig.dimensions.width &&
            page.pageConfig.dimensions.height &&
            page.pageConfig.dimensions.width === pages[0].pageConfig.dimensions.width &&
            page.pageConfig.dimensions.height === pages[0].pageConfig.dimensions.height);
    }
    arePageConfigDimensionsInvalid(page) {
        return page && page.loadState === LoadState.Finished && page.pageConfig.dimensions.width !== page.dimensions.width;
    }
    updatePageConfigsDimensions(dimensions) {
        this.pages.forEach(page => (page.pageConfig.dimensions = dimensions));
    }
}
//# sourceMappingURL=pdf-viewer-layout-one-column.js.map